import _ from "lodash";
import React, {
  isValidElement,
  cloneElement,
  useState,
  useEffect,
} from "react";

// Components
import I18nHOC from "UIComponents/I18nHOC";
import UIModal from "UIComponents/UIModal";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import AddAssignment from "ClassRoom/components/AddAssignment";
import AssessmentDraftModal from "./AssessmentDraftModal";
import LeSearchModal from "AppComponents/LeSearchModal";
import CreateAssignmentModal from "Worksheet/components/WorksheetRevamp/CreateAssignmentModal";

// DS
import {
  Dropdown,
  DropdownMenu,
  ButtonDisclosure,
  Button,
} from "@toddle-edu/ds-web";
import { AddOutlined } from "@toddle-edu/ds-icons";

// third party libs.
import PropTypes from "prop-types";
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";

// Modules
import {
  getAssignmentDropdownOptions,
  getEditAssignmentModalData,
} from "ClassRoom/modules/utils";
import { getSubjectsWithNoGradebook } from "GradingSetup/modules/Utils";
import {
  getDropdownOptions,
  onAddAssignmentItemClick,
} from "ClassRoom/modules/ClassRoomModule";
import { getGradeBasicDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  getIsCurrentAcademicYearSelected,
  getCurrentCurriculumProgramType,
  getIsArchivedClass,
  getCourseId,
  getUserType,
  getSelectedCurriculumId,
} from "modules/CommonSelectors";
import { getAssessmentDataFromLocalStorage } from "Assessments/modules/AssessmentModule";
import {
  getCurrentAssessmentTypes,
  setAiModuleMarketingKey,
} from "Platform/modules/PlatformModule";
import { getDefaultEntityType } from "ToddleLibrary/modules/ToddleLibraryUtils";

// Constants
import {
  STAFF,
  ASSESSMENT_TYPE_AI_TUTOR,
  CREATE_AI_TUTOR,
  CREATE_WORKSHEET,
  SUBJECT_TYPE_EE,
} from "Constants/stringConstants";

// Utils
import { isAITutorMarketingEnabled } from "Platform/modules/PlatformUtils";
import { AI_MODULES_FOR_MARKETING } from "Login/components/ChildrenPage/components/AIModuleMarketingModal/AIModuleMarketingUtils";
import {
  getIsAssessmentTypeConfigurationEnabled,
  getIsAITutorInEEEnabled,
} from "Utils/ACLStoreUtils";

// Scss
import classes from "./CreateOrEditAssignment.scss";
import { getSelectedLearningCourseId } from "LearningCourse/modules/Utils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";

const getDuplicateAssignmentHeader = ({ t, assessmentType }) => {
  switch (assessmentType) {
    case "qt":
      return t("classRoom:create_a_quick_task");
    case "le":
      return t("classRoom:create_le");
    case "fmt":
      return t("classRoom:create_fa");
    case "smt":
      return t("classRoom:create_sa");
    case "worksheet":
      return t("common:worksheet");
    case ASSESSMENT_TYPE_AI_TUTOR:
      return t("classRoom:create_an_ai_tutor");
    default:
      return t("classRoom:create_assessment_classroom");
  }
};

const getDuplicateAssignmentModalData = ({ t, assessmentType }) => {
  return {
    mode: "create",
    creationMode: "IMPORT_LE_FROM_LE_LIBRARY",
    steps: [
      {
        label: t("common:edit_with_label", {
          label: t("common:post"),
        }),
        value: "ASSESSMENT_DETAILS",
      },
      {
        label: t("classRoom:post_settings"),
        value: "SETTINGS",
        heading: t("classRoom:post_settings"),
        subHeading: t("classRoom:classes_discussion_shared_with"),
      },
    ],
    contentType: "ASSESSMENT",
    createHeader: getDuplicateAssignmentHeader({ t, assessmentType }),
    assessmentType,
  };
};

const CreateOrEditAssignment = props => {
  const {
    t,
    courseId,
    course,
    getDropdownOptions,
    getAssessmentDataFromLocalStorage,
    customButtonComponent,
    creationSource = "CLASSROOM",
    onUpdateFiltersLocally,
    onAddAssignmentItemClick,
    curriculumType,
    assignment,
    mode,
    onClose,
    addAssignmentProps = {},
    hideScheduleButton = false,
    getCurrentAssessmentTypes,
    createAssignmentData,
    isSettingsStepOnly = false,
    modalContent,
    overlayContainer,
    clonedAssessmentId: initialClonedAssessmentId = "",
    creationSubSource,
    testId = _.camelCase(creationSource),
    setToastMsg,
    isCurrentAcademicYearSelected,
    isArchivedClass,
    gradeId,
    learningCourseId,
    courseList,
    isMultipleCoursesSelectedInCalendar = false,
    selectedCourseIdInCalendar,
    selectedTimeSlot,
    onClickAssessmentDraftCloseButton = () => {},
    curriculumId,
  } = props;

  const subjectType = _.get(course, "subjects.0.type", "");

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
  const actualLearningCourseId = isLearningCoursesEnabled
    ? learningCourseId || course?.learningCourse?.[0]?.id || null
    : null;

  const disableActions = !isCurrentAcademicYearSelected || isArchivedClass;

  let globalGradeUid;
  if (gradeId) {
    const gradeDetails = getGradeBasicDetailsFromCache(gradeId);
    globalGradeUid = _.get(gradeDetails, "globalGrade.uid", null);
  }

  const assignmentDropdownOptions = getAssignmentDropdownOptions({
    curriculumType,
    globalGradeUid,
    curriculumId,
    subjectType,
  });

  const defaultEntityType = getDefaultEntityType({
    curriculumType,
    globalGradeUid,
  });

  const [assignmentModalData, setAssignmentModalData] = useState({});
  const [dialogBoxParams, setDialogBoxParams] = useState({});
  const [showLibrarySearchModal, setShowLibrarySearchModal] = useState(false);
  const [activeLibraryEntityType, setActiveLibraryEntityType] = useState(
    defaultEntityType
  );
  const [clonedAssessmentId, setClonedAssessmentId] = useState(
    initialClonedAssessmentId || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [sourceAssessmentId, setSourceAssessmentId] = useState("");
  const [showCreateAssignmentModal, setShowCreateAssignmentModal] = useState(
    false
  );

  useEffect(() => {
    if (mode === "edit" && assignment) {
      const { id, contentType } = assignment || {};

      // Extract label and icon from dropdown options
      const assessment = _.get(assignment, "content", {});
      const assessmentType = _.get(assessment, "assessmentType.value", "");

      let assessmentLabel;
      let icon;

      // Find the assessment type in dropdown options
      const createOptionsGroup = _.find(assignmentDropdownOptions, {
        key: "create",
      });

      if (createOptionsGroup) {
        const selectedOption = _.find(
          _.get(createOptionsGroup, "options", []),
          option => _.includes(_.toLower(option.key), _.toLower(assessmentType))
        );
        assessmentLabel = _.get(selectedOption, "label");
        icon = _.get(selectedOption, "icon");
      }

      const isAssessmentTypeConfigurationEnabled = getIsAssessmentTypeConfigurationEnabled();

      // Bundle parameters for getEditAssignmentModalData
      const editModalParams = {
        t,
        assignment,
        courseId,
        currentAssessmentTypes: getCurrentAssessmentTypes(),
      };

      // Add assessmentLabel and icon only if feature is enabled
      if (isAssessmentTypeConfigurationEnabled) {
        editModalParams.assessmentLabel = assessmentLabel;
        editModalParams.icon = icon;
      }

      const editAssignmentModalData = isSettingsStepOnly
        ? {
            steps: [
              {
                label: t("classRoom:assign_settings"),
                value: "SETTINGS",
                heading: t("classRoom:assign_settings"),
              },
            ],
          }
        : getEditAssignmentModalData(editModalParams);

      setAssignmentModalData({
        ...editAssignmentModalData,
        mode: "edit",
        contentType,
        id,
      });
    }
  }, [t, mode, assignment]);

  useEffect(() => {
    if (mode === "create" && !_.isEmpty(createAssignmentData)) {
      _onClickDropdownOption(createAssignmentData);
    }
  }, [mode, createAssignmentData]);

  useEffect(() => {
    if (mode === "duplicate" && assignment) {
      const assessment = _.get(assignment, "content", {});
      const assessmentType = _.get(assessment, "assessmentType.value", "");

      const duplicateAssignmentModalData = getDuplicateAssignmentModalData({
        t,
        assessmentType,
      });
      setAssignmentModalData(duplicateAssignmentModalData);
    }
  }, [t, mode, assignment]);

  const createAssessment = async ({
    key,
    eventTarget,
    assessmentId,
    templateId,
    label,
    icon,
    taskTypeId,
    taskType,
  }) => {
    const {
      assignmentModalData: localAssignmentModalData,
      clonedAssessmentId,
    } = await onAddAssignmentItemClick({
      t,
      key,
      curriculumProgramType: curriculumType,
      toggleLoading: setIsLoading,
      course,
      eventTarget,
      courseId,
      shouldCreateAssessment: _.isEmpty(assessmentId),
      learningCourseId: actualLearningCourseId,
      isMultipleCoursesSelectedInCalendar,
      isFromCalendar: creationSource === "CALENDAR",
      templateId,
      label,
      icon,
      taskTypeId,
      taskType,
    });

    const isImportOption = _.some(
      _.find(assignmentDropdownOptions, { key: "Assign" })?.options,
      { key }
    );

    if (
      clonedAssessmentId ||
      assessmentId ||
      isImportOption ||
      isMultipleCoursesSelectedInCalendar
    ) {
      if (clonedAssessmentId || assessmentId) {
        setClonedAssessmentId(clonedAssessmentId || assessmentId);
      }

      setAssignmentModalData(localAssignmentModalData);
    } else {
      setToastMsg("toastMsgs:something_went_wrong");
    }

    // * When revamp is enabled and the creation mode is worksheet
    const shouldShowCreateAssignmentModal =
      _.get(localAssignmentModalData, "creationMode") === CREATE_WORKSHEET &&
      !assessmentId &&
      !isMultipleCoursesSelectedInCalendar; // only when creating a new worksheet not on retrieving

    // * This is initializing step which is part of the worksheet revamp
    // * Opens up a modal to enter the worksheet basic details
    if (shouldShowCreateAssignmentModal) setShowCreateAssignmentModal(true);
  };

  const onClickImportFromToddleLibrary = async () => {
    for (const key of [
      "IMPORT_LE_FROM_TODDLE_LIBRARY",
      "IMPORT_WORKSHEET_FROM_TODDLE_LIBRARY",
    ]) {
      const {
        assessmentId,
        currentStep,
      } = await getAssessmentDataFromLocalStorage({
        creationMode: key,
        courseId: courseId || selectedCourseIdInCalendar,
      });

      if (assessmentId) {
        setDialogBoxParams({
          key,
          creationMode: key,
          assessmentId,
          currentStep,
        });
        return;
      }
    }

    setShowLibrarySearchModal(true);
  };

  const _onClickDropdownOption = async ({ key, eventTarget }) => {
    if (key === "IMPORT_FROM_TODDLE_LIBRARY") {
      onClickImportFromToddleLibrary();
      return;
    }

    if (key === CREATE_AI_TUTOR) {
      if (isAITutorMarketingEnabled()) {
        props.setAiModuleMarketingKey(AI_MODULES_FOR_MARKETING.AI_TUTOR);
        return;
      }
    }

    // Extract from dropdown options
    let templateId;
    let label;
    let icon;
    let taskTypeId;
    let taskType;
    const createOptionsGroup = _.find(assignmentDropdownOptions, {
      key: "create",
    });

    if (!_.isEmpty(createOptionsGroup)) {
      const selectedOption = _.find(_.get(createOptionsGroup, "options", []), {
        key,
      });
      templateId = _.get(selectedOption, "templateId");
      label = _.get(selectedOption, "label");
      icon = _.get(selectedOption, "icon");
      taskTypeId = _.get(selectedOption, "taskTypeId");
      taskType = _.get(selectedOption, "taskType");
    }

    if (isMultipleCoursesSelectedInCalendar && key !== "QUICK_TASK") {
      await createAssessment({
        key,
        eventTarget,
        templateId,
        label,
        icon,
        taskTypeId,
        taskType,
      });
      return;
    }

    const {
      assessmentId,
      currentStep,
    } = await getAssessmentDataFromLocalStorage({
      creationMode: key,
      courseId: courseId || selectedCourseIdInCalendar,
    });

    if (assessmentId) {
      setDialogBoxParams({
        key,
        creationMode: key,
        assessmentId,
        currentStep,
        templateId,
        taskTypeId,
        label,
        icon,
        taskType,
      });
    } else {
      await createAssessment({
        key,
        eventTarget,
        templateId,
        label,
        icon,
        taskTypeId,
        taskType,
      });
    }
  };

  const renderCreateAssignmentButton = () => {
    if (mode !== "create" || !_.isEmpty(createAssignmentData)) return null;

    if (
      _.includes(
        ["RECORD_OF_EXPERIMENTAL_PROGRAMMES", "PRACTICAL_ACTIVITIES"],
        creationSource
      )
    ) {
      return isValidElement(customButtonComponent)
        ? cloneElement(customButtonComponent, {
            onClick: () =>
              _onClickDropdownOption({
                key:
                  creationSource === "PRACTICAL_ACTIVITIES"
                    ? "CREATE_DP_PA"
                    : "CREATE_DP_EP",
              }),
          })
        : null;
    }

    const subjectsWithoutGradebook = getSubjectsWithNoGradebook();
    const hasOnlyQuickTask =
      _.includes(subjectsWithoutGradebook, subjectType) &&
      !(subjectType == SUBJECT_TYPE_EE && getIsAITutorInEEEnabled());

    return hasOnlyQuickTask ? (
      <Button
        testId={`${testId}-createOrEditAssignment-new`}
        icon={<AddOutlined />}
        dsVersion="2.0"
        variant={"primary"}
        type="fill"
        size="medium"
        disabled={disableActions}
        className={classes.createAssignmentButton}
        onClick={() => _onClickDropdownOption({ key: "CREATE_QUICK_TASK" })}
        shortcut={{ code: "KeyC" }}
      >
        {t("common:new_with_label", {
          label: t("common:task_label"),
        })}
      </Button>
    ) : (
      <Dropdown
        disabled={disableActions}
        dsVersion="2.0"
        overlay={
          <DropdownMenu
            dsVersion="2.0"
            testId={`${testId}-createOrEditAssignment`}
            options={getDropdownOptions({
              options: assignmentDropdownOptions,
              t,
            })}
            overlayHeight="fit-content"
            onShortcutPress={e =>
              _onClickDropdownOption({
                key: e.key,
              })
            }
            onClick={e =>
              _onClickDropdownOption({
                key: e.key,
              })
            }
          />
        }
      >
        {isValidElement(customButtonComponent) ? (
          cloneElement(customButtonComponent, {
            disabled: disableActions,
            shortcut: { code: "KeyC" },
          })
        ) : (
          <ButtonDisclosure
            dsVersion="2.0"
            testId={`${testId}-createOrEditAssignment-new`}
            id={"COACHMARKS_IMPORT_LE_FROM_COMMUNITY_CLASSROOM"}
            icon={<AddOutlined />}
            variant={"primary"}
            type="fill"
            size="medium"
            disabled={disableActions}
            className={classes.createAssignmentButton}
            shortcut={{ code: "KeyC" }}
          >
            {t("common:create_new")}
          </ButtonDisclosure>
        )}
      </Dropdown>
    );
  };

  const creationMode = dialogBoxParams?.creationMode;
  const isAssessmentImportedFromToddleLibrary = _.includes(
    ["IMPORT_LE_FROM_TODDLE_LIBRARY", "IMPORT_WORKSHEET_FROM_TODDLE_LIBRARY"],
    creationMode
  );

  const handleAssessmentDraftModalClose = isCloseButtonClicked => {
    setDialogBoxParams({});
    if (isAssessmentImportedFromToddleLibrary) {
      setShowLibrarySearchModal(true);
    }

    // special case for calendar overlay click
    if (isCloseButtonClicked) {
      onClickAssessmentDraftCloseButton?.();
    }
  };

  const onCreateNewAssignment = () => {
    if (!isAssessmentImportedFromToddleLibrary)
      createAssessment({
        key: _.get(dialogBoxParams, "key"),
        templateId: _.get(dialogBoxParams, "templateId"),
        taskTypeId: _.get(dialogBoxParams, "taskTypeId"),
        label: _.get(dialogBoxParams, "label"),
        icon: _.get(dialogBoxParams, "icon"),
        taskType: _.get(dialogBoxParams, "taskType"),
      });
  };

  const onSuccessLeSearch = async assessmentId => {
    await createAssessment({
      key: _.isEqual(activeLibraryEntityType, "learning_experience")
        ? "IMPORT_LE_FROM_TODDLE_LIBRARY"
        : "IMPORT_WORKSHEET_FROM_TODDLE_LIBRARY",
      assessmentId,
    });
  };

  const importFromToddleLibraryModal = () => {
    return showLibrarySearchModal ? (
      <LeSearchModal
        testId={`${testId}-importFromLibrary`}
        onLeSearchModalClose={({ assessmentId }) => {
          setShowLibrarySearchModal(false);
          setActiveLibraryEntityType(defaultEntityType);
          setSourceAssessmentId(assessmentId);
        }}
        portalType={"COMMUNITY"}
        importFromToddleLibrary={true}
        endpoint={"CLASS_STREAM"}
        onSuccess={assessmentId => onSuccessLeSearch(assessmentId)}
        assessmentTypes={[]}
        activeLibraryEntityType={activeLibraryEntityType}
        setActiveLibraryEntityType={setActiveLibraryEntityType}
      />
    ) : null;
  };

  const handleCreateAssignmentModalSubmit = () => {
    setShowCreateAssignmentModal(false);
  };

  const handleCreateAssignmentModalClose = () => {
    // * On close Assignment Modal, Resetting state
    setShowCreateAssignmentModal(false);
    setAssignmentModalData({});
    setShowLibrarySearchModal(false);
    onClose?.(true);
    setSourceAssessmentId("");
  };

  return (
    <>
      {renderCreateAssignmentButton()}

      {importFromToddleLibraryModal()}

      {showCreateAssignmentModal && (
        <CreateAssignmentModal
          course={course}
          assessmentId={clonedAssessmentId}
          onSubmit={handleCreateAssignmentModalSubmit}
          onClose={handleCreateAssignmentModalClose}
        />
      )}

      {!_.isEmpty(assignmentModalData) && !showCreateAssignmentModal ? (
        <UIModal
          isOpen={true}
          isFullScreen={true}
          modalContent={modalContent}
          overlayContainer={overlayContainer}
          tabIndexValue={0}
        >
          <AddAssignment
            course={course}
            courseId={course?.id || courseId}
            courseTitle={course?.title}
            mode={assignmentModalData["mode"]}
            creationMode={assignmentModalData["creationMode"]}
            steps={assignmentModalData["steps"]}
            assignmentId={assignmentModalData["id"]}
            contentType={assignmentModalData["contentType"]}
            createHeader={assignmentModalData["createHeader"]}
            cancelBtnText={assignmentModalData["cancelBtnText"]}
            doneBtnText={assignmentModalData["doneBtnText"]}
            editHeaderText={assignmentModalData["editHeaderText"]}
            assessmentType={assignmentModalData["assessmentType"]}
            initialStep={assignmentModalData["initialStep"]}
            taskTypeGroup={assignmentModalData["taskTypeGroup"]}
            dynamicAssessmentIcon={assignmentModalData["icon"]}
            assignmentTemplateId={assignmentModalData["templateId"]}
            assignmentTaskTypeId={assignmentModalData["taskTypeId"]}
            creationSource={creationSource}
            onClose={isSuccess => {
              setAssignmentModalData({});
              setShowLibrarySearchModal(false);
              onClose?.(isSuccess);
              setSourceAssessmentId("");
            }}
            clonedAssessmentId={clonedAssessmentId}
            subjects={_.get(course, "subjects", [])}
            grades={_.get(course, "grades", [])}
            onChangeAssignmentStateTab={onUpdateFiltersLocally}
            {...addAssignmentProps}
            hideScheduleButton={hideScheduleButton}
            isDuplicateAssignment={mode === "duplicate"}
            creationSubSource={creationSubSource}
            sourceAssessmentId={sourceAssessmentId}
            sourceAssignmentId={_.get(assignment, "id", null)}
            testId={`${testId}-createOrEditAssignment`}
            courseList={courseList}
            selectedTimeSlot={selectedTimeSlot}
            learningCourseId={actualLearningCourseId}
          />
        </UIModal>
      ) : null}

      {!_.isEmpty(dialogBoxParams) ? (
        <AssessmentDraftModal
          testId={`${testId}-createOrEditAssignment`}
          onClose={handleAssessmentDraftModalClose}
          onCreateNewAssignment={onCreateNewAssignment}
          onRetrieveAssignment={() => createAssessment(dialogBoxParams)}
          courseId={courseId}
          {...dialogBoxParams}
        />
      ) : null}

      {isLoading ? (
        <FullScreenLoader overlayContainer={classes.overlayContainer} />
      ) : null}
    </>
  );
};

const CreateOrEditAssignmentWrapper = props => {
  const { userType } = props;

  if (userType === STAFF) {
    return <CreateOrEditAssignment {...props} />;
  }

  return null;
};

CreateOrEditAssignmentWrapper.propTypes = {
  course: PropTypes.object,
  customButtonComponent: PropTypes.element,
  creationSource: PropTypes.string,
  mode: PropTypes.string,
  addAssignmentProps: PropTypes.object,
  hideScheduleButton: PropTypes.bool,
  createAssignmentData: PropTypes.object,
  modalContent: PropTypes.string,
  overlayContainer: PropTypes.string,
};

CreateOrEditAssignmentWrapper.defaultProps = {
  course: {},
  customButtonComponent: null,
  creationSource: "CLASSROOM",
  mode: "create",
  addAssignmentProps: {},
  hideScheduleButton: false,
  createAssignmentData: {}, // create assignment directly with key without rendering button,
  modalContent: "",
  overlayContainer: "",
};

const mapStateToProps = (state, ownProps) => {
  const isCurrentAcademicYearSelected = getIsCurrentAcademicYearSelected(state);
  const isArchivedClass = getIsArchivedClass(state);
  const userType = getUserType(state);
  const courseId = ownProps?.courseId || getCourseId(state);
  const curriculumType = getCurrentCurriculumProgramType(state);
  const curriculumId = getSelectedCurriculumId(state);

  const gradeId = _.get(state, "teacher.selected_class.selected_grades[0]", "");

  return {
    courseId,
    curriculumType,
    isCurrentAcademicYearSelected,
    isArchivedClass,
    gradeId,
    userType,
    learningCourseId: getSelectedLearningCourseId(state),
    curriculumId,
  };
};

const mapActionCreators = {
  getDropdownOptions,
  getAssessmentDataFromLocalStorage,
  onAddAssignmentItemClick,
  getCurrentAssessmentTypes,
  setAiModuleMarketingKey,
  setToastMsg,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators)
)(CreateOrEditAssignmentWrapper);
